import copy from '../../../locale/de-DE/copy.json';
import removeEmptyFields from '../removeEmptyFields';
import { updateSubmitedValues } from './formatData';
import { getDateFormatFromTimestamp } from '../pdf/formatData';

export const getCopyByKey = (key) => copy[key];

export const getLabelValueObject = (labelKey, value) => {
  const valueToUse = value || '';
  if (!Array.isArray(labelKey)) {
    const label = getCopyByKey(labelKey);
    return { [label]: valueToUse };
  }
  const labels = labelKey.map((key) => getCopyByKey(key));
  return { [labels.join(' ')]: valueToUse };
};

export const generateDocumentAttributes = ({
  pcn,
  updatedSubmitedValues: {
    firstName,
    lastName,
    dateOfBirth,
    postalCode,
  },
}) => (
  {
    documentAttributes: removeEmptyFields({
      PROCESS_CONTROL_NO: pcn,
      CUST_FIRST_NM: firstName,
      CUST_LAST_NM: lastName,
      CUST_BIRTH_DATE: dateOfBirth,
      PSTL_AREA_CD: postalCode,
      MKT_NM: 'DE',
    }),
  }
);

export const generateMetaFileData = () => {
  const data = {
    fileType: 'PDF',
    countryCode: 'DE',
    sourceSystem: 'EAPPLY',
    channel: 'WEB',
    journey: 'SAS',
  };
  return data;
};

export const generateProductSummaryStage = ({
  pct,
  logo,
  sourcecode,
  productName,
  pcn,
}) => (
  {
    stageHeader: getCopyByKey('archive.productInformation'),
    stageData: {
      PCT: pct,
      LOGO: logo,
      'Source Code': sourcecode,
      'Product Name': productName,
      PCN: pcn,
    },
  }
);

export const generatePersonalInformationStage = ({
  updatedSubmitedValues: {
    prefix,
    title,
    firstName,
    middleName,
    lastName,
    nameOnCard,
    dateOfBirth,
    email,
    mobilePhoneNumber,
    plasticColor,
  },
}) => {
  const data = {
    stageHeader: getCopyByKey('tracker.step1'),
    stageData: {
      ...getLabelValueObject('prefix.label', prefix),
      ...getLabelValueObject('title.label', title),
      ...getLabelValueObject('firstName.label', firstName),
      ...getLabelValueObject('middleName.label', middleName),
      ...getLabelValueObject('lastName.label', lastName),
      ...getLabelValueObject('nameOnCard.label', nameOnCard),
      ...getLabelValueObject(['dateofBirth.label.text', 'dateofBirth.label.dateFormat'], dateOfBirth),
      ...getLabelValueObject('email.label', email),
      ...getLabelValueObject('mobilePhoneNumber.label', `+49 ${mobilePhoneNumber}`),
      ...getLabelValueObject('plasticColor.header.text', plasticColor),
    },
  };
  return data;
};

export const generateContactDetailsStage = ({
  updatedSubmitedValues: {
    streetName,
    houseNumber,
    postalCode,
    location,
    placeOfBirth,
    nationality,
  },
}) => {
  const data = {
    stageHeader: getCopyByKey('tracker.step2'),
    stageData: {
      ...getLabelValueObject('streetName.label', streetName),
      ...getLabelValueObject('houseNumber.label', houseNumber),
      ...getLabelValueObject('postalCode.label', postalCode),
      ...getLabelValueObject('location.label', location),
      ...getLabelValueObject('placeOfBirth.label', placeOfBirth),
      ...getLabelValueObject('nationality.label', nationality),
    },
  };
  return data;
};

export const generateFinancialInformationDetailsStage = ({
  updatedSubmitedValues: {
    profession,
    industry,
    occupation,
  },
}) => {
  const data = {
    stageHeader: getCopyByKey('tracker.step3'),
    stageData: {
      ...getLabelValueObject('profession.label', profession),
      ...getLabelValueObject('industry.label', industry),
      ...getLabelValueObject('occupation.label', occupation),
    },
  };
  return data;
};

export const generateDataProtectionStage = () => {
  const data = {
    stageHeader: getCopyByKey('formPageContent1'),
    additionalInfo: `${getCopyByKey('formPageContent2')} ${getCopyByKey('websitelink')}`,
  };
  return data;
};

export const generateTermsOfServiceStage = ({ basicProductName }) => {
  const data = {
    stageHeader: getCopyByKey('formPageContent3'),
    additionalInfo: [
      `<p>${getCopyByKey('formPageContent4')}</p>`,
      '<ul>',
      `<li>${getCopyByKey('formPageBullet1')}</li>`,
      `<li>${getCopyByKey([basicProductName])}</li>`,
      `<li>${getCopyByKey('formPageBullet3')}</li>`,
      `<li>${getCopyByKey('formPageBullet4')}</li>`,
      `<li>${getCopyByKey('formPageBullet5')}</li>`,
      `<li>${getCopyByKey('formPageBullet6')}</li>`,
      '</ul>',
      `<p>${getCopyByKey('formPageBulletSubText1')} `,
      `${getCopyByKey('formPageSubtextLink')}`,
      ` ${getCopyByKey('formPageBulletSubText2')}</p>`,
    ].join(''),
  };
  return data;
};

export const generateOrderWithPaymentStage = ({
  submitTimestamp,
}) => {
  const { date, hour, min } = getDateFormatFromTimestamp(submitTimestamp);
  const data = {
    stageHeader: getCopyByKey('archive.orderWithPayment.header'),
    stageData: {
      ...getLabelValueObject('archive.submitTimestamp.label', `${date} ${hour}:${min}`),
      ...getLabelValueObject('archive.ipAddress.label', 'ipAddress'),
    },
  };
  return data;
};

const generateArchiveData = ({
  pct,
  logo,
  sourcecode,
  productName,
  pcn,
  submitValues,
  basicProductName,
  submitTimestamp,
}) => {
  const updatedSubmitedValues = updateSubmitedValues(submitValues);
  const documentAttributes = generateDocumentAttributes({ pcn, updatedSubmitedValues });
  const metaFileData = generateMetaFileData();
  const productSummaryStage = generateProductSummaryStage({
    pct,
    logo,
    sourcecode,
    productName,
    pcn,
  });
  const personalInformationStage = generatePersonalInformationStage({ updatedSubmitedValues });
  const contactDetailsStage = generateContactDetailsStage({ updatedSubmitedValues });
  const financialInformationDetailsStage = generateFinancialInformationDetailsStage({
    updatedSubmitedValues,
  });
  const dataProtectionStage = generateDataProtectionStage();
  const termsOfServiceStage = generateTermsOfServiceStage({ basicProductName });
  const orderWithPaymentStage = generateOrderWithPaymentStage({
    submitTimestamp,
  });

  return removeEmptyFields({
    ...documentAttributes,
    ...metaFileData,
    data: {
      stageDetails: [
        productSummaryStage,
        personalInformationStage,
        contactDetailsStage,
        financialInformationDetailsStage,
        dataProtectionStage,
        termsOfServiceStage,
        orderWithPaymentStage,
      ],
      header: getCopyByKey('archive.application.title'),
      pcn,
    },
  });
};

export default generateArchiveData;
